/**
 * Copyright (c) 2023, WSO2 LLC. (https://www.wso2.com). All Rights Reserved.
 *
 * WSO2 LLC. licenses this file to you under the Apache License,
 * Version 2.0 (the "License"); you may not use this file except
 * in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing,
 * software distributed under the License is distributed on an
 * "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY
 * KIND, either express or implied. See the License for the
 * specific language governing permissions and limitations
 * under the License.
 */

import {
  Timeline,
  timelineItemClasses
} from "@mui/lab";
import { Box, Card, CardContent, Container, Stack, Typography } from "@mui/material";
import PropTypes from "prop-types";
import React, { useState } from "react";
import "./HistoryItems.css";
import HistoryItemTimelineWrapper from "./HistoryItemTimelineWrapper";

/**
 * HistoryItems component
 * Description - This component is used to display the history items
 */
function HistoryItems({ data }) {
  const [ isCollapsed, setIsCollapsed ] = useState(true);

  const handleToggleCollapse = () => {
    setIsCollapsed(!isCollapsed);
  };

  return (
    <>
      { Object.keys(data).length > 0 &&  Object.keys(data).map((key, index) => (
        <Box key={ key + index }>
          { /* Render month and year */ }
          <Typography variant="h5" fontWeight={ "normal" }>
            { new Date(key.split("-")[0], key.split("-")[1] - 1, 1).toLocaleString("default", {
              month: "long",
              year: "numeric"
            }) }
          </Typography>

          { /* Render timeline */ }
          { data[key].incidents.length > 0 && (
            <Timeline
              position="right"
              sx={ {
                [`& .${timelineItemClasses.root}:before`]: {
                  flex: 0,
                  padding: 0
                },
                mb: 0
              } }
            >
              { /* Render incident list */ }
              { data[key].incidents.slice(0, isCollapsed ? 3 : data[key].incidents.length).map((incident) => (
                <HistoryItemTimelineWrapper key={ incident.begin } history={ incident } />
              )) }
            </Timeline>) }

          { /* Expand and collapse */ }
          { data[key].incidents.length > 3 && (
            <Stack sx={ { mb: 2 } }>
              <Card elevation={ 0 } variant="outlined">
                <CardContent className="collapse-content">
                  <Typography variant="body2" onClick={ handleToggleCollapse }>
                    { isCollapsed ? `Show All ${data[key].incidents.length} Incidents` : "Collapse Incidents" }
                  </Typography>
                </CardContent>
              </Card>
            </Stack>
          ) }

          { /* Display no incidents reported if the incident lenght is 0 */ }
          { data[key].incidents.length === 0 && (
            <Container>
              <Stack>
                <Card elevation={ 0 } variant="outlined" sx={ { border: 0 } }>
                  <CardContent sx={ { pl: 0 } }>
                    <Typography variant="body1" color={ "text.secondary" }>
                      No incidents reported this month.
                    </Typography>
                  </CardContent>
                </Card>
              </Stack>
            </Container>
          ) }
        </Box>
      )) }
    </>
  );
}

HistoryItems.propTypes = {
  data: PropTypes.object
};

export default HistoryItems;
